import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Disc, Users, TrendingUp, CalendarDays, CalendarRange, Plus, ArrowRight, KeyRound, CheckCircle, WrenchIcon, Disc3, Inbox, Megaphone, Send, Loader2 } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";

function StatCard({ icon: Icon, label, value, sub, color = "rose" }) {
  const colors = {
    rose: "text-rose-400 bg-rose-900/20 border-rose-800/30",
    blue: "text-blue-400 bg-blue-900/20 border-blue-800/30",
    emerald: "text-emerald-400 bg-emerald-900/20 border-emerald-800/30",
    amber: "text-amber-400 bg-amber-900/20 border-amber-800/30",
  };
  return (
    <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${colors[color]}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-white text-2xl font-black font-heading">{value?.toLocaleString("de") ?? "–"}</p>
        {sub && <p className="text-zinc-600 text-xs mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const { settings, refresh } = useSettings();
  const isDeveloper = user?.role === "developer";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingProposals, setPendingProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(true);

  useEffect(() => {
    api.get("/settings/stats")
      .then(res => { setStats(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!token) return;
    api.get("/release-proposals/admin?status=pending&page=1&limit=5", {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { setPendingProposals(res.data?.items || []); })
      .catch(() => {})
      .finally(() => setProposalsLoading(false));
  }, [token]);

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-black text-white font-heading">Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Übersicht &amp; Statistiken</p>
        </div>
        <Link
          to="/admin/releases/new"
          data-testid="new-release-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm rounded-lg transition-colors"
        >
          <Plus size={14} /> Neues Release
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-900 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Disc} label="Releases gesamt" value={stats?.releases} color="rose" />
            <StatCard icon={CalendarDays} label="Diese Woche" value={stats?.new_this_week} sub="neue Releases" color="blue" />
            <StatCard icon={CalendarRange} label="Diesen Monat" value={stats?.new_this_month} sub="neue Releases" color="emerald" />
            <StatCard icon={Users} label="Alle Benutzer" value={stats?.total_users} color="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top Artists */}
            {stats?.top_artists?.length > 0 && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-sm flex items-center gap-2">
                    <TrendingUp size={14} className="text-rose-400" /> Top Artists
                  </h2>
                  <Link to="/admin/releases" className="text-zinc-600 hover:text-rose-400 text-xs flex items-center gap-1 transition-colors">
                    Alle <ArrowRight size={11} />
                  </Link>
                </div>
                <div className="space-y-2">
                  {stats.top_artists.map((a, i) => (
                    <div key={a.interpret} className="flex items-center gap-3">
                      <span className="text-zinc-700 text-xs w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-zinc-300 text-xs font-medium truncate">{a.interpret}</span>
                          <span className="text-zinc-600 text-xs ml-2 flex-shrink-0">{a.count}</span>
                        </div>
                        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-rose-600 rounded-full"
                            style={{ width: `${(a.count / stats.top_artists[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Latest Releases */}
            {stats?.latest_releases?.length > 0 && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-sm flex items-center gap-2">
                    <Disc size={14} className="text-rose-400" /> Zuletzt hinzugefügt
                  </h2>
                  <Link to="/admin/releases" className="text-zinc-600 hover:text-rose-400 text-xs flex items-center gap-1 transition-colors">
                    Alle <ArrowRight size={11} />
                  </Link>
                </div>
                <div className="space-y-2">
                  {stats.latest_releases.map((r) => (
                    <Link
                      key={r.id}
                      to={`/admin/releases/${r.id}/edit`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded bg-zinc-800 overflow-hidden flex-shrink-0">
                        {r.cover_url ? (
                          <img src={r.cover_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc size={12} className="text-zinc-700" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-300 text-xs font-semibold truncate group-hover:text-white transition-colors">
                          {r.interpret}
                        </p>
                        <p className="text-zinc-600 text-xs truncate">{r.titel}</p>
                      </div>
                      {r.typ && (
                        <span className="text-zinc-700 text-xs flex-shrink-0">{r.typ}</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Release-Vorschläge Widget */}
      <div className="mt-5 bg-zinc-900 border border-white/5 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm flex items-center gap-2">
            <Disc3 size={14} className="text-rose-400" /> Release-Vorschläge
            {pendingProposals.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-600/20 text-rose-400 text-[10px] font-black border border-rose-500/20">
                {pendingProposals.length}
              </span>
            )}
          </h2>
          <Link to="/admin/release-proposals" className="text-zinc-600 hover:text-rose-400 text-xs flex items-center gap-1 transition-colors">
            Alle <ArrowRight size={11} />
          </Link>
        </div>
        {proposalsLoading ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => <div key={i} className="h-10 bg-zinc-800 rounded-lg animate-pulse" />)}
          </div>
        ) : pendingProposals.length === 0 ? (
          <div className="flex items-center gap-2 py-3 text-zinc-600 text-xs">
            <Inbox size={14} /> Keine ausstehenden Vorschläge
          </div>
        ) : (
          <div className="space-y-1.5">
            {pendingProposals.map(p => (
              <Link
                key={p.id}
                to="/admin/release-proposals"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                  {p.cover_url
                    ? <img src={p.cover_url} alt={p.titel} className="w-full h-full object-cover" onError={e => e.target.style.display = "none"} />
                    : <div className="w-full h-full flex items-center justify-center"><Disc3 size={10} className="text-zinc-700" /></div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-300 text-xs font-semibold truncate group-hover:text-white transition-colors">{p.interpret}</p>
                  <p className="text-zinc-600 text-xs truncate">{p.titel}</p>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 flex-shrink-0 font-semibold">
                  {p.submitted_by_name || "Artist"}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {isDeveloper && (
        <BroadcastWidget token={token} />
      )}
      {isDeveloper && (
        <MaintenanceToggle
          active={settings?.maintenance_mode || false}
          message={settings?.maintenance_message || ""}
          onToggle={refresh}
        />
      )}
      <ChangePasswordForm />
      </div>
    </AdminLayout>
  );
}

function BroadcastWidget({ token }) {
  const [title, setTitle]     = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink]       = useState("");
  const [targets, setTargets] = useState(["all"]);
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);
  const headers               = { Authorization: `Bearer ${token}` };

  const TARGET_OPTS = [
    { key: "all",        label: "Alle User",     color: "text-zinc-300" },
    { key: "supporters", label: "Nur Supporter",  color: "text-rose-400"    },
    { key: "labels",     label: "Nur Labels",     color: "text-purple-400"  },
    { key: "artists",    label: "Nur Artists",    color: "text-blue-400"    },
  ];

  const toggleTarget = (key) => {
    if (key === "all") { setTargets(["all"]); return; }
    setTargets(prev => {
      const without = prev.filter(t => t !== "all" && t !== key);
      return prev.includes(key) ? (without.length ? without : ["all"]) : [...without, key];
    });
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true); setResult(null);
    try {
      const res = await api.post("/notifications/admin/broadcast", { title, message, link, targets }, { headers });
      setResult({ ok: true, count: res.data.sent_to });
      setTitle(""); setMessage(""); setLink("");
      setTimeout(() => setResult(null), 5000);
    } catch { setResult({ ok: false }); }
    setSending(false);
  };

  return (
    <div className="mt-6 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #111 0%, #0d0d0d 100%)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 4px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(236,72,153,0.12)", border: "1px solid rgba(236,72,153,0.2)" }}>
          <Megaphone size={15} className="text-rose-400" />
        </div>
        <div>
          <p className="text-white text-sm font-bold">Push-Nachricht senden</p>
          <p className="text-zinc-600 text-xs">Nur Dev · In-App + Browser Push</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Zielgruppe */}
        <div>
          <p className="text-zinc-600 text-xs font-semibold uppercase tracking-widest mb-2">Empfänger</p>
          <div className="flex flex-wrap gap-2">
            {TARGET_OPTS.map(opt => (
              <button
                key={opt.key}
                onClick={() => toggleTarget(opt.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  targets.includes(opt.key)
                    ? "bg-white/[0.10] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                    : "bg-zinc-900 text-zinc-600 hover:bg-zinc-800 hover:text-zinc-400"
                } ${opt.color}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Titel */}
        <div>
          <p className="text-zinc-600 text-xs font-semibold uppercase tracking-widest mb-1.5">Titel</p>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={80}
            placeholder="Kurzer Titel (max. 80 Zeichen)"
            className="w-full bg-zinc-900 text-white text-sm rounded-xl px-4 py-2.5 outline-none placeholder:text-zinc-700 focus:ring-1 focus:ring-rose-500/40 transition-all"
            style={{ border: "1px solid rgba(255,255,255,0.06)" }}
          />
        </div>

        {/* Nachricht */}
        <div>
          <p className="text-zinc-600 text-xs font-semibold uppercase tracking-widest mb-1.5">Nachricht</p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Deine Nachricht an die Community…"
            className="w-full bg-zinc-900 text-white text-sm rounded-xl px-4 py-2.5 outline-none placeholder:text-zinc-700 focus:ring-1 focus:ring-rose-500/40 transition-all resize-none"
            style={{ border: "1px solid rgba(255,255,255,0.06)" }}
          />
          <p className="text-zinc-700 text-[11px] text-right mt-0.5">{message.length}/2000</p>
        </div>

        {/* Link (optional) */}
        <div>
          <p className="text-zinc-600 text-xs font-semibold uppercase tracking-widest mb-1.5">Link <span className="text-zinc-700 normal-case">(optional)</span></p>
          <input
            value={link}
            onChange={e => setLink(e.target.value)}
            placeholder="/releases oder https://…"
            className="w-full bg-zinc-900 text-white text-sm rounded-xl px-4 py-2.5 outline-none placeholder:text-zinc-700 focus:ring-1 focus:ring-rose-500/40 transition-all"
            style={{ border: "1px solid rgba(255,255,255,0.06)" }}
          />
        </div>

        {/* Senden */}
        <div className="flex items-center justify-between pt-1">
          <div>
            {result?.ok && <p className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5"><CheckCircle size={12} /> An {result.count} User gesendet</p>}
            {result?.ok === false && <p className="text-red-400 text-xs">Fehler beim Senden</p>}
          </div>
          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg,#e11d48,#be123c)", boxShadow: "0 4px 16px rgba(225,29,72,0.3)" }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Senden…" : "Senden"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaintenanceToggle({ active, message, onToggle }) {
  const [toggling, setToggling] = useState(false);
  const [msg, setMsg] = useState(message);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await api.put("/settings/maintenance");
      onToggle();
    } catch { /* silent */ }
    finally { setToggling(false); }
  };

  const handleSaveMsg = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/settings/", { maintenance_message: msg });
      onToggle();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <div className={`mt-6 rounded-xl border p-5 transition-colors ${active ? "bg-amber-950/20 border-amber-700/30" : "bg-zinc-900 border-white/5"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <WrenchIcon size={14} className={active ? "text-amber-400" : "text-zinc-500"} />
          <span className="text-white font-bold text-sm">Wartungsmodus</span>
          {active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-bold border border-amber-500/30">
              AKTIV
            </span>
          )}
        </div>
        <button
          onClick={handleToggle}
          disabled={toggling}
          data-testid="maintenance-toggle-btn"
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${active ? "bg-amber-500" : "bg-zinc-700"} ${toggling ? "opacity-50" : ""}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${active ? "left-5" : "left-0.5"}`} />
        </button>
      </div>
      <p className="text-zinc-500 text-xs mb-3">
        Wenn aktiv sehen nicht-eingeloggte Nutzer eine Wartungsseite. Admins & Developer haben weiterhin vollen Zugriff.
      </p>
      <div className="flex gap-2">
        <input
          value={msg}
          onChange={e => setMsg(e.target.value)}
          placeholder="Nachricht auf der Wartungsseite..."
          className="flex-1 bg-zinc-800 border border-zinc-700 text-white text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
          data-testid="maintenance-message-input"
        />
        <button
          onClick={handleSaveMsg}
          disabled={saving}
          className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
        >
          {saved ? <><CheckCircle size={12} className="text-green-400" /> Gespeichert</> : saving ? "…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  return (
    <div className="mt-6 bg-zinc-900 border border-white/5 rounded-xl p-5">
      <h2 className="text-white font-bold text-sm flex items-center gap-2 mb-3">
        <KeyRound size={14} className="text-rose-400" /> Passwort
      </h2>
      <p className="text-zinc-500 text-sm mb-3">
        Das Passwort wird über Auth0 verwaltet.
      </p>
      <a
        href="https://handsupwillneverdie.eu.auth0.com/dbconnections/change_password"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-sm rounded-lg transition-colors border border-zinc-700"
      >
        <KeyRound size={13} /> Passwort bei Auth0 zurücksetzen
      </a>
    </div>
  );
}
